import React, { useState, useEffect } from "react";
import {
  List,
  Rate,
  Avatar,
  Space,
  Typography,
  Button,
  Select,
  Checkbox,
  Empty,
  Spin,
  message,
  Image,
  Tag,
  Divider,
  Card,
} from "antd";
import {
  LikeOutlined,
  LikeFilled,
  DislikeOutlined,
  DislikeFilled,
  CheckCircleFilled,
  PlayCircleOutlined,
  UserOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import API_CONFIG from "../../config/api";
import moment from "moment";
import "moment/locale/fr";

moment.locale("fr");

const { Text, Title, Paragraph } = Typography;
const { Option } = Select;

/**
 * Interface pour un avis client
 */
interface Review {
  id: number;
  product_id: number;
  customer_id: number;
  rating: number;
  title: string | null;
  comment: string;
  verified_purchase: boolean;
  has_media: boolean;
  helpful_count: number;
  not_helpful_count: number;
  merchant_response: string | null;
  merchant_response_at: string | null;
  customer_name: string;
  customer_location: string | null;
  created_at: string;
  media: ReviewMedia[];
  user_vote?: "helpful" | "not_helpful" | null;
}

/**
 * Interface pour les médias d'un avis
 */
interface ReviewMedia {
  id: number;
  media_type: "photo" | "video";
  media_url: string;
  thumbnail_url: string | null;
}

/**
 * Props du composant ReviewsList
 */
interface ReviewsListProps {
  /** ID du produit */
  productId: number;

  /** Slug de la boutique */
  storeSlug: string;

  /** Session ID pour les votes anonymes */
  sessionId?: string;

  /** Token client JWT (si authentifié) */
  customerToken?: string | null;

  /** Nombre d'avis par page (défaut: 10) */
  pageSize?: number;

  /** Afficher les filtres (défaut: true) */
  showFilters?: boolean;
}

/**
 * Composant ReviewsList
 *
 * Affiche la liste des avis clients approuvés pour un produit
 * avec tri, filtres, pagination, et système de votes d'utilité
 *
 * @example
 * <ReviewsList
 *   productId={123}
 *   storeSlug="ma-boutique"
 *   sessionId="abc-123-def"
 *   customerToken={token}
 *   showFilters
 * />
 */
const ReviewsList: React.FC<ReviewsListProps> = ({
  productId,
  storeSlug,
  sessionId,
  customerToken,
  pageSize = 10,
  showFilters = true,
}) => {
  // États
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState<string>("recent");
  const [filterRating, setFilterRating] = useState<number | null>(null);
  const [onlyWithMedia, setOnlyWithMedia] = useState(false);
  const [votingReviewId, setVotingReviewId] = useState<number | null>(null);

  /**
   * Charge les avis depuis l'API
   */
  const fetchReviews = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pageSize.toString(),
        sort: sortBy,
      });

      if (filterRating) {
        params.append("rating", filterRating.toString());
      }

      if (onlyWithMedia) {
        params.append("with_media", "true");
      }

      const response = await fetch(
        `${API_CONFIG.PUBLIC_STORE_API}/${storeSlug}/products/${productId}/reviews?${params}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();

      if (data.success) {
        setReviews(data.reviews);
        setTotal(data.pagination.total);
      } else {
        message.error("Erreur lors du chargement des avis");
      }
    } catch (error) {
      console.error("Erreur fetch avis:", error);
      message.error("Impossible de charger les avis");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Charge les avis au montage et lors des changements de filtres
   */
  useEffect(() => {
    fetchReviews();
  }, [page, sortBy, filterRating, onlyWithMedia]);

  /**
   * Gère le vote d'utilité sur un avis
   */
  const handleVote = async (reviewId: number, voteType: "helpful" | "not_helpful") => {
    if (!sessionId && !customerToken) {
      message.warning("Connectez-vous pour voter");
      return;
    }

    setVotingReviewId(reviewId);

    try {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };

      if (sessionId) {
        headers["X-Session-ID"] = sessionId;
      }

      if (customerToken) {
        headers["Authorization"] = `Bearer ${customerToken}`;
      }

      const response = await fetch(
        `${API_CONFIG.PUBLIC_STORE_API}/${storeSlug}/reviews/helpful-vote`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            reviewId,
            voteType,
          }),
        }
      );

      const data = await response.json();

      if (data.success) {
        // Mettre à jour l'avis localement
        setReviews((prevReviews) =>
          prevReviews.map((review) => {
            if (review.id === reviewId) {
              return {
                ...review,
                helpful_count: data.review.helpful_count,
                not_helpful_count: data.review.not_helpful_count,
                user_vote: voteType,
              };
            }
            return review;
          })
        );

        message.success("Vote enregistré !");
      } else {
        message.error(data.message || "Erreur lors du vote");
      }
    } catch (error) {
      console.error("Erreur vote:", error);
      message.error("Impossible d'enregistrer votre vote");
    } finally {
      setVotingReviewId(null);
    }
  };

  /**
   * Anonymise le nom du client (ex: "Amina Diallo" => "Amina D.")
   */
  const anonymizeName = (name: string): string => {
    if (!name) return "Client anonyme";
    const parts = name.trim().split(" ");
    if (parts.length === 1) return parts[0];
    return `${parts[0]} ${parts[parts.length - 1].charAt(0)}.`;
  };

  /**
   * Formate la date en relatif (ex: "Il y a 2 jours")
   */
  const formatDate = (dateString: string): string => {
    return moment(dateString).fromNow();
  };

  /**
   * Rendu d'un avis
   */
  const renderReview = (review: Review) => {
    const isHelpfulVoted = review.user_vote === "helpful";
    const isNotHelpfulVoted = review.user_vote === "not_helpful";

    return (
      <List.Item key={review.id} style={{ padding: "20px 0" }}>
        <Card
          bordered={false}
          style={{
            width: "100%",
            backgroundColor: "#fafafa",
            borderRadius: 8,
          }}
        >
          {/* En-tête avis : Avatar + Nom + Date */}
          <Space direction="vertical" size={12} style={{ width: "100%" }}>
            <Space size={12} align="start">
              <Avatar
                size={48}
                icon={<UserOutlined />}
                style={{ backgroundColor: "#1890ff" }}
              />
              <Space direction="vertical" size={2} style={{ flex: 1 }}>
                <Space size={8} align="center">
                  <Text strong style={{ fontSize: 16 }}>
                    {anonymizeName(review.customer_name)}
                  </Text>
                  {review.verified_purchase && (
                    <Tag
                      icon={<CheckCircleFilled />}
                      color="success"
                      style={{ margin: 0 }}
                    >
                      Achat vérifié
                    </Tag>
                  )}
                </Space>
                <Space size={8} align="center">
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    {formatDate(review.created_at)}
                  </Text>
                  {review.customer_location && (
                    <>
                      <Text type="secondary">•</Text>
                      <Text type="secondary" style={{ fontSize: 13 }}>
                        {review.customer_location}
                      </Text>
                    </>
                  )}
                </Space>
              </Space>
            </Space>

            {/* Note étoiles */}
            <Rate
              disabled
              allowHalf
              value={review.rating}
              style={{ fontSize: 16, color: "#faad14" }}
            />

            {/* Titre de l'avis */}
            {review.title && (
              <Title level={5} style={{ margin: 0, fontWeight: 600 }}>
                {review.title}
              </Title>
            )}

            {/* Commentaire */}
            <Paragraph style={{ margin: 0, fontSize: 14, lineHeight: 1.6 }}>
              {review.comment}
            </Paragraph>

            {/* Galerie médias (photos + vidéos) */}
            {review.has_media && review.media && review.media.length > 0 && (
              <Space size={8} wrap>
                {review.media.map((media) => {
                  if (media.media_type === "photo") {
                    return (
                      <Image
                        key={media.id}
                        src={media.media_url}
                        alt="Photo avis client"
                        width={100}
                        height={100}
                        style={{
                          objectFit: "cover",
                          borderRadius: 8,
                          cursor: "pointer",
                        }}
                        preview={{
                          mask: <EyeOutlined style={{ fontSize: 20 }} />,
                        }}
                      />
                    );
                  } else if (media.media_type === "video") {
                    return (
                      <div
                        key={media.id}
                        style={{
                          position: "relative",
                          width: 100,
                          height: 100,
                          borderRadius: 8,
                          overflow: "hidden",
                          cursor: "pointer",
                        }}
                        onClick={() => window.open(media.media_url, "_blank")}
                      >
                        <img
                          src={media.thumbnail_url || "/placeholder-video.jpg"}
                          alt="Miniature vidéo"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                        <PlayCircleOutlined
                          style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            fontSize: 32,
                            color: "#fff",
                          }}
                        />
                      </div>
                    );
                  }
                  return null;
                })}
              </Space>
            )}

            {/* Boutons votes utilité */}
            <Space size={16}>
              <Button
                type={isHelpfulVoted ? "primary" : "text"}
                icon={isHelpfulVoted ? <LikeFilled /> : <LikeOutlined />}
                onClick={() => handleVote(review.id, "helpful")}
                loading={votingReviewId === review.id}
                disabled={!!review.user_vote}
              >
                Utile ({review.helpful_count})
              </Button>
              <Button
                type={isNotHelpfulVoted ? "primary" : "text"}
                danger={isNotHelpfulVoted}
                icon={isNotHelpfulVoted ? <DislikeFilled /> : <DislikeOutlined />}
                onClick={() => handleVote(review.id, "not_helpful")}
                loading={votingReviewId === review.id}
                disabled={!!review.user_vote}
              >
                Pas utile ({review.not_helpful_count})
              </Button>
            </Space>

            {/* Réponse du marchand */}
            {review.merchant_response && (
              <>
                <Divider style={{ margin: "12px 0" }} />
                <Card
                  size="small"
                  style={{
                    backgroundColor: "#f0f5ff",
                    border: "1px solid #d6e4ff",
                  }}
                >
                  <Space direction="vertical" size={4} style={{ width: "100%" }}>
                    <Text strong style={{ color: "#1890ff" }}>
                      Réponse du marchand
                    </Text>
                    <Text style={{ fontSize: 13 }}>
                      {review.merchant_response}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {formatDate(review.merchant_response_at!)}
                    </Text>
                  </Space>
                </Card>
              </>
            )}
          </Space>
        </Card>
      </List.Item>
    );
  };

  return (
    <div style={{ width: "100%" }}>
      {/* Barre de filtres et tri */}
      {showFilters && (
        <Space
          size={16}
          wrap
          style={{
            marginBottom: 20,
            padding: "16px",
            backgroundColor: "#fafafa",
            borderRadius: 8,
            width: "100%",
          }}
        >
          {/* Tri */}
          <Space direction="vertical" size={4}>
            <Text strong style={{ fontSize: 13 }}>
              Trier par
            </Text>
            <Select
              value={sortBy}
              onChange={(value) => {
                setSortBy(value);
                setPage(1);
              }}
              style={{ width: 180 }}
            >
              <Option value="recent">Plus récents</Option>
              <Option value="helpful">Plus utiles</Option>
              <Option value="rating_desc">Meilleure note</Option>
              <Option value="rating_asc">Moins bonne note</Option>
            </Select>
          </Space>

          {/* Filtre par note */}
          <Space direction="vertical" size={4}>
            <Text strong style={{ fontSize: 13 }}>
              Note
            </Text>
            <Select
              value={filterRating}
              onChange={(value) => {
                setFilterRating(value);
                setPage(1);
              }}
              style={{ width: 150 }}
              allowClear
              placeholder="Toutes les notes"
            >
              {[5, 4, 3, 2, 1].map((star) => (
                <Option key={star} value={star}>
                  {star} étoile{star > 1 ? "s" : ""}
                </Option>
              ))}
            </Select>
          </Space>

          {/* Filtre avec médias */}
          <Checkbox
            checked={onlyWithMedia}
            onChange={(e) => {
              setOnlyWithMedia(e.target.checked);
              setPage(1);
            }}
            style={{ marginTop: 20 }}
          >
            Avec photos/vidéos uniquement
          </Checkbox>
        </Space>
      )}

      {/* Liste des avis */}
      <Spin spinning={loading}>
        {reviews.length === 0 && !loading ? (
          <Empty
            description="Aucun avis pour ce produit"
            style={{ margin: "40px 0" }}
          />
        ) : (
          <List
            dataSource={reviews}
            renderItem={renderReview}
            pagination={{
              current: page,
              pageSize: pageSize,
              total: total,
              onChange: (newPage) => setPage(newPage),
              showSizeChanger: false,
              showTotal: (total) => `${total} avis`,
              style: { textAlign: "center", marginTop: 20 },
            }}
          />
        )}
      </Spin>
    </div>
  );
};

export default ReviewsList;
